(function () {
  const storageKey = 'ubackdrop.discountCode';
  const storageDurationMs = 24 * 60 * 60 * 1000;
  const messageNotEligible = 'This product is not eligible for this discount code.';
  const messageBig20MinimumRequirement = 'This discount code is only valid for orders that meet the minimum purchase requirements. Please try applying it during checkout.';
  const messageApplied = 'Discount code applied.';
  const messageValidationUnavailable = 'Discount validation is unavailable. Please try again later.';
  let cartDiscountRefreshTimer = null;
  let activeProductDiscountCode = '';

  function getStoredCode() {
    const storedValue = window.localStorage.getItem(storageKey);
    if (!storedValue) return '';

    try {
      const storedDiscount = JSON.parse(storedValue);
      if (typeof storedDiscount === 'string') {
        const legacyCode = storedDiscount.trim().toUpperCase();
        if (legacyCode) setStoredCode(legacyCode);
        return legacyCode;
      }

      if (!storedDiscount || typeof storedDiscount !== 'object') {
        window.localStorage.removeItem(storageKey);
        return '';
      }

      if (storedDiscount.expiresAt && Date.now() > storedDiscount.expiresAt) {
        window.localStorage.removeItem(storageKey);
        return '';
      }

      return (storedDiscount.code || '').trim().toUpperCase();
    } catch (error) {
      const legacyCode = storedValue.trim().toUpperCase();
      if (legacyCode) {
        setStoredCode(legacyCode);
      }
      return legacyCode;
    }
  }

  function setStoredCode(code) {
    const value = (code || '').trim().toUpperCase();
    if (value) {
      window.localStorage.setItem(
        storageKey,
        JSON.stringify({
          code: value,
          expiresAt: Date.now() + storageDurationMs,
        })
      );
    } else {
      window.localStorage.removeItem(storageKey);
    }
    return value;
  }

  function escapeHtml(value) {
    return String(value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
  }

  function formatUsdMoney(cents) {
    const amount = Number(cents) / 100;
    return `$${amount.toLocaleString('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })} USD`;
  }

  function formatDiscountMoneyText(cents, displayContext) {
    if (!displayContext?.currencyCode || !Number.isFinite(displayContext.rate) || displayContext.rate <= 0) {
      return formatUsdMoney(cents);
    }

    const amount = (Number(cents) / 100) * displayContext.rate;
    const formattedAmount = amount.toLocaleString('en-US', {
      minimumFractionDigits: displayContext.decimals,
      maximumFractionDigits: displayContext.decimals,
    });
    const prefix = displayContext.prefix || '';
    return `${prefix}${formattedAmount} ${displayContext.currencyCode}`.trim();
  }

  function formatDiscountMoneyMarkup(cents, displayContext) {
    const safeCents = Math.max(0, Math.round(Number(cents) || 0));
    const currencyCode = displayContext?.currencyCode || 'USD';
    const moneyText = displayContext ? formatDiscountMoneyText(safeCents, displayContext) : formatUsdMoney(safeCents);
    return `<span class="money" data-discount-code-money doubly-currency-usd="${safeCents}" doubly-currency="${escapeHtml(currencyCode)}">${escapeHtml(moneyText)}</span>`;
  }

  function normalizeCurrencyCode(value) {
    const code = (value || '').trim().toUpperCase();
    return /^[A-Z]{3}$/.test(code) ? code : '';
  }

  function isElementVisible(element) {
    return !!(element?.offsetWidth || element?.offsetHeight || element?.getClientRects?.().length);
  }

  function getDoublyCurrencyFromText(text) {
    const cleanText = (text || '').trim();
    const currencyLabels = {
      'US DOLLAR': 'USD',
      'AUSTRALIAN DOLLAR': 'AUD',
      'CANADIAN DOLLAR': 'CAD',
      EURO: 'EUR',
      'BRITISH POUND STERLING': 'GBP',
    };
    const labelCurrency = currencyLabels[cleanText.toUpperCase()];
    if (labelCurrency) return labelCurrency;

    const match = cleanText.match(/\b[A-Z]{3}\b/);
    return normalizeCurrencyCode(match?.[0]);
  }

  function getActiveDoublyCurrency() {
    const syncedCurrency = window.UBackdropCurrencySync?.getActiveCurrency?.();
    if (syncedCurrency) return syncedCurrency;

    const visiblePriceCurrency = Array.from((getProductPriceContainer() || document).querySelectorAll('.money'))
      .filter(isElementVisible)
      .map((element) => getDoublyCurrencyFromText(element.textContent))
      .find(Boolean);
    if (visiblePriceCurrency) return visiblePriceCurrency;

    const visibleCurrency = Array.from(document.querySelectorAll('.doubly-nice-select .current, .doubly-float .current'))
      .filter(isElementVisible)
      .map((element) => getDoublyCurrencyFromText(element.textContent))
      .find(Boolean);
    if (visibleCurrency) return visibleCurrency;

    const priceCurrency = Array.from((getProductPriceContainer() || document).querySelectorAll('.money'))
      .map((element) => getDoublyCurrencyFromText(element.textContent) || normalizeCurrencyCode(element.getAttribute('doubly-currency')))
      .find(Boolean);
    if (priceCurrency) return priceCurrency;

    const candidates = [
      document.querySelector('.doubly-nice-select .option.selected')?.getAttribute('data-value'),
      document.querySelector('.doubly-nice-select .option.selected')?.textContent,
      document.querySelector('.doubly-nice-select .option.focus')?.getAttribute('data-value'),
      document.querySelector('.doubly-nice-select .option.focus')?.textContent,
      document.querySelector('.currency-switcher-btn.selected')?.getAttribute('doubly-currency'),
      document.querySelector('.currency-switcher-btn.selected')?.textContent,
      document.querySelector('[name="doubly-currencies"]')?.value,
      window.DoublyGlobalCurrency?.currentCurrency,
      window.DoublyCurrency?.currentCurrency,
    ];

    for (const candidate of candidates) {
      const currency = normalizeCurrencyCode(candidate) || getDoublyCurrencyFromText(candidate);
      if (currency) return currency;
    }

    try {
      const cookieCurrency = (window.DoublyGlobalCurrency || window.DoublyCurrency)?.cookie?.read?.();
      const currency = normalizeCurrencyCode(cookieCurrency);
      if (currency) return currency;
    } catch (error) {
      // Doubly cookie access can be blocked in previews; fall back to visible labels.
    }

    return '';
  }

  function parseVisibleMoneyText(text) {
    const cleanText = (text || '').trim();
    const amountMatch = cleanText.match(/\d[\d,]*(?:\.\d+)?/);
    if (!amountMatch) return null;

    const amount = Number(amountMatch[0].replace(/,/g, ''));
    if (!Number.isFinite(amount) || amount <= 0) return null;

    const prefix = cleanText.slice(0, amountMatch.index).trim();
    const decimalPart = amountMatch[0].split('.')[1] || '';
    return {
      amount,
      prefix,
      decimals: decimalPart.length || 2,
    };
  }

  function getActiveMoneyDisplayContext(baseCents) {
    const baseAmount = Number(baseCents) / 100;
    if (!Number.isFinite(baseAmount) || baseAmount <= 0) {
      return null;
    }

    const visibleMoney = Array.from((getProductPriceContainer() || document).querySelectorAll('.money'))
      .filter(isElementVisible)
      .find((element) => getDoublyCurrencyFromText(element.textContent) || normalizeCurrencyCode(element.getAttribute('doubly-currency')));
    if (!visibleMoney) return null;

    const parsedMoney = parseVisibleMoneyText(visibleMoney.textContent);
    const currencyCode =
      getDoublyCurrencyFromText(visibleMoney.textContent) ||
      normalizeCurrencyCode(visibleMoney.getAttribute('doubly-currency')) ||
      getActiveDoublyCurrency() ||
      'USD';

    if (!parsedMoney) return null;

    const attrUsdCents = Number(visibleMoney.getAttribute('doubly-currency-usd'));
    const sourceUsdAmount = Number.isFinite(attrUsdCents) && attrUsdCents > 0 ? attrUsdCents / 100 : baseAmount;
    return {
      currencyCode,
      rate: parsedMoney.amount / sourceUsdAmount,
      prefix: parsedMoney.prefix,
      decimals: parsedMoney.decimals,
    };
  }

  function restoreDoublyCurrencyControls(currencyCode) {
    const activeCurrency = normalizeCurrencyCode(currencyCode);
    if (!activeCurrency) return;

    if (window.UBackdropCurrencySync?.restoreControls) {
      window.UBackdropCurrencySync.restoreControls(activeCurrency);
      return;
    }

    document.querySelectorAll('[name="doubly-currencies"]').forEach((select) => {
      const hasOption = Array.from(select.options || []).some((option) => option.value === activeCurrency);
      if (hasOption) select.value = activeCurrency;
    });

    document.querySelectorAll('.doubly-nice-select').forEach((select) => {
      const options = Array.from(select.querySelectorAll('.option'));
      const activeOption = options.find(
        (option) =>
          normalizeCurrencyCode(option.getAttribute('data-value')) === activeCurrency ||
          getDoublyCurrencyFromText(option.textContent) === activeCurrency
      );
      if (!activeOption) return;

      options.forEach((option) => option.classList.remove('selected', 'focus'));
      activeOption.classList.add('selected', 'focus');

      const current = select.querySelector('.current');
      if (current) current.innerHTML = activeOption.innerHTML;
    });

    document.querySelectorAll('.currency-switcher-btn[doubly-currency]').forEach((button) => {
      button.classList.toggle('selected', button.getAttribute('doubly-currency') === activeCurrency);
    });
  }

  function refreshDiscountMoneyMarkup(currencyCode, options = {}) {
    const activeCurrency = normalizeCurrencyCode(currencyCode) || getActiveDoublyCurrency();
    if (!activeCurrency) return;

    restoreDoublyCurrencyControls(activeCurrency);
    if (options.skipConvert) return;

    try {
      if (window.UBackdropCurrencySync?.applyCurrency) {
        window.UBackdropCurrencySync.applyCurrency(activeCurrency, { selector: '[data-discount-code-money]' });
      } else {
        const doublyApi = window.DoublyGlobalCurrency || window.DoublyCurrency;
        if (doublyApi?.convertAll) {
          doublyApi.convertAll(activeCurrency, '[data-discount-code-money]');
        }
      }
    } catch (error) {
      // Doubly is an external app script; discount validation must not depend on its refresh hook.
    }
  }

  function restoreActiveDoublyCurrency(currencyCode, options = {}) {
    const activeCurrency = normalizeCurrencyCode(currencyCode);
    if (!activeCurrency) return;

    restoreDoublyCurrencyControls(activeCurrency);
    if (options.skipConvert) return;

    try {
      if (window.UBackdropCurrencySync?.applyCurrency) {
        window.UBackdropCurrencySync.applyCurrency(activeCurrency);
      } else {
        const doublyApi = window.DoublyGlobalCurrency || window.DoublyCurrency;
        if (doublyApi?.convertAll) {
          doublyApi.convertAll(activeCurrency);
        }
      }
    } catch (error) {
      // Keep the selected currency controls intact even if the app conversion hook is unavailable.
    }

    restoreDoublyCurrencyControls(activeCurrency);
  }

  function moneyAmountToCents(amount) {
    return Math.round(Number(amount || 0) * 100);
  }

  function getProductDiscountBox() {
    return document.querySelector('[data-discount-code-product]');
  }

  function getCurrentVariantId() {
    return document.querySelector('product-info .product-variant-id')?.value || document.querySelector('product-form [name="id"]')?.value;
  }

  function getCurrentQuantity() {
    const input = document.querySelector('product-info quantity-input input[name="quantity"], product-form input[name="quantity"]');
    const quantity = Number(input?.value || 1);
    return Number.isFinite(quantity) && quantity > 0 ? quantity : 1;
  }

  function getVariantPrice(variantId) {
    const box = getProductDiscountBox();
    const variants = JSON.parse(box?.querySelector('[data-discount-variants]')?.textContent || '[]');
    return variants.find((variant) => String(variant.id) === String(variantId));
  }

  function isBig20MinimumRequirementCase(code) {
    const box = getProductDiscountBox();
    return (code || '').trim().toUpperCase() === 'BIG20' && box?.dataset.bigEventSaleProduct === 'true';
  }

  function getProductDiscountIneligibleMessage(code) {
    return isBig20MinimumRequirementCase(code) ? messageBig20MinimumRequirement : messageNotEligible;
  }

  function setProductMessage(type, text) {
    const message = document.querySelector('[data-discount-code-message]');
    if (!message) return;
    message.textContent = text || '';
    message.dataset.state = type || '';
  }

  function getProductPriceContainer() {
    return document.querySelector('product-info [id^="price-"] .price__container');
  }

  function restoreProductPrice() {
    const container = getProductPriceContainer();
    if (!container || !container.dataset.discountOriginalHtml) return;

    container.innerHTML = container.dataset.discountOriginalHtml;
    delete container.dataset.discountOriginalHtml;
    container.closest('.price')?.classList.remove('discount-code-price-active');
  }

  function setProductPreview(preview, currencyCode, options = {}) {
    const result = document.querySelector('[data-discount-code-result]');
    const container = getProductPriceContainer();

    if (!preview) {
      restoreProductPrice();
      if (result) {
        result.hidden = true;
        result.innerHTML = '';
      }
      return;
    }

    if (!container) return;
    if (!container.dataset.discountOriginalHtml) {
      container.dataset.discountOriginalHtml = container.innerHTML;
    }

    container.closest('.price')?.classList.add('discount-code-price-active');
    container.innerHTML = `
      <div class="price__regular">
        <span class="visually-hidden visually-hidden--inline">Sale price</span>
        <span class="price-item price-item--sale price-item--last">${preview.finalPrice}</span>
        <span>
          <s class="price-item price-item--regular">${preview.originalPrice}</s>
        </span>
      </div>
    `;
    refreshDiscountMoneyMarkup(currencyCode, { skipConvert: options.skipDoublyConvert });

    if (result) {
      result.hidden = true;
      result.innerHTML = '';
    }
  }

  async function previewProductDiscount(code) {
    const variantId = getCurrentVariantId();
    const variant = getVariantPrice(variantId);

    if (!variantId || !variant) {
      setProductMessage('error', messageNotEligible);
      setProductPreview(null);
      return false;
    }

    if (!window.ShopifyStorefrontAPI?.previewCartDiscount) {
      setStoredCode('');
      syncCheckoutDiscountInputs('');
      setProductMessage('error', messageValidationUnavailable);
      setProductPreview(null);
      return false;
    }

    const quantity = getCurrentQuantity();
    const displayContext = getActiveMoneyDisplayContext(Number(variant.price));
    const activeCurrency = displayContext?.currencyCode || getActiveDoublyCurrency();
    const line = {
      merchandiseId: `gid://shopify/ProductVariant/${variantId}`,
      quantity,
    };

    const cart = await window.ShopifyStorefrontAPI.previewCartDiscount([line], code);
    const discountCode = cart.discountCodes?.find((item) => item.code?.toUpperCase() === code.toUpperCase()) || cart.discountCodes?.[0];
    const lineCost = cart.lines?.nodes?.[0]?.cost;
    const originalCents = Number(variant.price) * quantity;
    const finalCents = moneyAmountToCents(lineCost?.totalAmount?.amount);
    const hasLineDiscount = finalCents > 0 && finalCents < originalCents;

    if (!discountCode?.applicable || !hasLineDiscount) {
      setStoredCode('');
      syncCheckoutDiscountInputs('');
      setProductMessage('error', getProductDiscountIneligibleMessage(code));
      setProductPreview(null);
      renderCartDiscountState(null, '');
      return false;
    }

    setStoredCode(code);
    setProductMessage('success', messageApplied);
    setProductPreview({
      originalPrice: formatDiscountMoneyMarkup(originalCents, displayContext),
      finalPrice: formatDiscountMoneyMarkup(finalCents, displayContext),
    }, activeCurrency, { skipDoublyConvert: !!displayContext });
    restoreActiveDoublyCurrency(activeCurrency, { skipConvert: !!displayContext });
    await applyDiscountToCart(code, { refresh: false });
    restoreActiveDoublyCurrency(activeCurrency, { skipConvert: !!displayContext });
    return true;
  }

  async function applyDiscountToCart(code, options = {}) {
    const cleanCode = (code || '').trim().toUpperCase();
    if (!cleanCode || !window.routes?.cart_update_url) return null;

    const response = await fetch(window.routes.cart_update_url, {
      ...fetchConfig(),
      body: JSON.stringify({ discount: cleanCode }),
    });

    if (!response.ok) {
      return null;
    }

    const cart = await response.json();
    if (cart?.errors) {
      return null;
    }

    if (options.refresh) {
      publishCartUpdate(cart);
    }

    setStoredCode(cleanCode);
    renderCartDiscountState(cart, cleanCode);
    return cart;
  }

  function publishCartUpdate(cart) {
    if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof publish === 'function') {
      publish(PUB_SUB_EVENTS.cartUpdate, {
        source: 'discount-code-sync',
        cartData: cart,
      });
    }
  }

  function cartHasDiscount(cart) {
    if (!cart) return false;
    if (Number(cart.total_discount || 0) > 0) return true;
    if (Array.isArray(cart.cart_level_discount_applications) && cart.cart_level_discount_applications.length > 0) return true;
    if (Array.isArray(cart.discount_applications) && cart.discount_applications.length > 0) return true;
    return false;
  }

  function getCartDiscountCode(cart, fallbackCode) {
    const cartLevelDiscount = cart?.cart_level_discount_applications?.find((discount) => discount?.title);
    if (cartLevelDiscount?.title) return cartLevelDiscount.title.trim().toUpperCase();

    const discount = cart?.discount_applications?.find((item) => item?.title);
    if (discount?.title) return discount.title.trim().toUpperCase();

    const fallback = (fallbackCode || '').trim().toUpperCase();
    if (fallback) return fallback;

    return '';
  }

  function renderCartDiscountState(cart, code) {
    const cleanCode = getCartDiscountCode(cart, code || getStoredCode());
    document.querySelectorAll('[data-active-discount-code]').forEach((element) => {
      const hasDiscount = cart ? cartHasDiscount(cart) : element.dataset.cartHasDiscount === 'true';
      if (!cleanCode || !hasDiscount) {
        element.hidden = true;
        element.querySelector('[data-active-discount-code-value]').textContent = '';
        return;
      }

      element.hidden = false;
      element.querySelector('[data-active-discount-code-value]').textContent = cleanCode;
      const warning = element.querySelector('[data-active-discount-code-warning]');
      if (warning) warning.hidden = true;
    });
  }

  function getCartJsonUrl() {
    const cartUrl = window.routes?.cart_url || '/cart';
    return cartUrl.endsWith('.js') ? cartUrl : `${cartUrl.replace(/\/$/, '')}.js`;
  }

  async function fetchCartState() {
    const response = await fetch(getCartJsonUrl(), {
      headers: {
        Accept: 'application/json',
      },
    });

    if (!response.ok) return null;
    return response.json();
  }

  function scheduleCartDiscountRefresh(delay = 80) {
    clearTimeout(cartDiscountRefreshTimer);
    cartDiscountRefreshTimer = setTimeout(initCartDiscount, delay);
  }

  function observeCartDiscountNodes() {
    if (window.discountCodeCartObserverBound) return;
    window.discountCodeCartObserverBound = true;

    const observer = new MutationObserver((mutations) => {
      const hasCartDiscountNode = mutations.some((mutation) =>
        Array.from(mutation.addedNodes).some(
          (node) =>
            node.nodeType === 1 &&
            (node.matches?.('[data-active-discount-code], .cart-drawer__footer, cart-drawer-items') ||
              node.querySelector?.('[data-active-discount-code], .cart-drawer__footer, cart-drawer-items'))
        )
      );

      if (hasCartDiscountNode) {
        scheduleCartDiscountRefresh();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  function getActiveDiscountCode() {
    const storedCode = getStoredCode();
    if (storedCode) return storedCode;

    const formCode = getCheckoutDiscountForms()
      .map((form) => form.querySelector('input[name="discount"][data-discount-code-checkout]')?.value?.trim())
      .find(Boolean);
    if (formCode) return formCode;

    const visibleCode = Array.from(document.querySelectorAll('[data-active-discount-code]'))
      .find((element) => !element.hidden)
      ?.querySelector('[data-active-discount-code-value]')
      ?.textContent
      ?.trim();

    return visibleCode || '';
  }

  function getCheckoutDiscountForms() {
    return Array.from(document.querySelectorAll('form#cart, form#CartDrawer-Form'));
  }

  function cleanupNonCheckoutDiscountInputs() {
    document.querySelectorAll('input[name="discount"][data-discount-code-checkout]').forEach((input) => {
      if (!input.closest('form#cart, form#CartDrawer-Form')) {
        input.remove();
      }
    });
  }

  function syncCheckoutDiscountInputs(code) {
    cleanupNonCheckoutDiscountInputs();
    const cleanCode = typeof code === 'string' ? code.trim().toUpperCase() : getActiveDiscountCode();
    getCheckoutDiscountForms().forEach((form) => {
      let input = form.querySelector('input[name="discount"][data-discount-code-checkout]');
      if (!cleanCode) {
        if (input) input.remove();
        return;
      }

      if (!input) {
        input = document.createElement('input');
        input.type = 'hidden';
        input.name = 'discount';
        input.dataset.discountCodeCheckout = 'true';
        form.appendChild(input);
      }

      input.value = cleanCode;
    });
  }

  function bindCheckoutDiscountSync() {
    if (window.discountCodeCheckoutSyncBound) return;
    window.discountCodeCheckoutSyncBound = true;

    document.addEventListener(
      'click',
      (event) => {
        const checkoutButton = event.target.closest('button[name="checkout"], input[name="checkout"]');
        if (!checkoutButton) return;
        syncCheckoutDiscountInputs();
      },
      true
    );

    document.addEventListener(
      'submit',
      (event) => {
        if (!event.target.matches('form#cart, form#CartDrawer-Form')) return;
        syncCheckoutDiscountInputs();
      },
      true
    );
  }

  function bindCartDiscountEvents() {
    if (window.discountCodeCartEventsBound) return;
    window.discountCodeCartEventsBound = true;

    if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
        renderCartDiscountState(event?.cartData || null, getStoredCode());
        scheduleCartDiscountRefresh(180);
      });
    }
  }

  function updateCheckoutLinks(code) {
    const cleanCode = code || getActiveDiscountCode();
    if (!cleanCode) return;
    syncCheckoutDiscountInputs(cleanCode);
  }

  function initProductBox() {
    const box = getProductDiscountBox();
    if (!box || box.dataset.discountCodeReady === 'true') return;

    box.dataset.discountCodeReady = 'true';
    const input = box.querySelector('[data-discount-code-input]');
    const form = box.querySelector('[data-discount-code-form]');

    async function applyProductDiscountFromInput() {
      const code = input.value.trim().toUpperCase();
      if (!code) {
        setStoredCode('');
        activeProductDiscountCode = '';
        setProductMessage('', '');
        setProductPreview(null);
        syncCheckoutDiscountInputs('');
        return;
      }

      box.classList.add('is-loading');
      setProductMessage('', '');
      try {
        const applied = await previewProductDiscount(code);
        if (applied) {
          activeProductDiscountCode = code;
          updateCheckoutLinks(code);
        } else {
          activeProductDiscountCode = '';
        }
      } catch (error) {
        setStoredCode('');
        activeProductDiscountCode = '';
        syncCheckoutDiscountInputs('');
        setProductMessage('error', messageNotEligible);
        setProductPreview(null);
        renderCartDiscountState(null, '');
      } finally {
        box.classList.remove('is-loading');
      }
    }

    form.addEventListener('submit', async (event) => {
      event.preventDefault();
      await applyProductDiscountFromInput();
    });

    if (typeof PUB_SUB_EVENTS !== 'undefined' && typeof subscribe === 'function') {
      subscribe(PUB_SUB_EVENTS.variantChange, () => {
        if (!activeProductDiscountCode) {
          setProductPreview(null);
          return;
        }
        input.value = activeProductDiscountCode;
        form.requestSubmit();
      });
    }
  }

  async function initCartDiscount() {
    const code = getStoredCode();
    renderCartDiscountState(null, code);
    updateCheckoutLinks(code);

    try {
      const cart = await fetchCartState();
      if (!cart) return;
      const cartCode = getCartDiscountCode(cart, code);
      renderCartDiscountState(cart, cartCode);
      updateCheckoutLinks(cartCode);
    } catch (error) {
      renderCartDiscountState(null, code);
    }
  }

  function init() {
    initProductBox();
    initCartDiscount();
    bindCheckoutDiscountSync();
    bindCartDiscountEvents();
    observeCartDiscountNodes();
  }

  document.addEventListener('DOMContentLoaded', init);
  document.addEventListener('shopify:section:load', init);
  document.addEventListener('cart:updated', initCartDiscount);

  if (window.ShopifyStorefrontAPI?.onInit) {
    window.ShopifyStorefrontAPI.onInit(initProductBox);
  }
})();
